import React from 'react';
import { useNavigate } from 'react-router-dom';
import TabNavigation from '../components/TabNavigation';
import { useModuleAccess } from '../hooks/useModuleAccess';

const SafetyHub = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isModuleEnabled, loading } = useModuleAccess();

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Safety Management</h1>
          <p className="text-gray-600 mt-2">Comprehensive safety management tools and modules</p>
        </div>

        {/* Safety Management Modules */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* HIRA Module */}
          {isModuleEnabled('hira') && (
            <button
              onClick={() => navigate('/hira')}
              className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-red-700 mb-2">⚠️</div>
              <h3 className="text-lg font-bold text-red-900 mb-1">HIRA</h3>
              <p className="text-sm text-red-600">Hazard Identification & Risk Assessment</p>
            </button>
          )}

          {/* PTW Module */}
          {isModuleEnabled('ptw') && (
            <button
              onClick={() => navigate('/ptw')}
              className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-green-700 mb-2">🛡️</div>
              <h3 className="text-lg font-bold text-green-900 mb-1">Permit to Work</h3>
              <p className="text-sm text-green-600">Manage work permits for high-risk activities</p>
            </button>
          )}

          {/* CAPA Module */}
          {isModuleEnabled('capa') && (
            <button
              onClick={() => navigate('/capa')}
              className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-orange-700 mb-2">✅</div>
              <h3 className="text-lg font-bold text-orange-900 mb-1">CAPA Management</h3>
              <p className="text-sm text-orange-600">Manage corrective & preventive actions</p>
            </button>
          )}

          {/* Training Module */}
          {isModuleEnabled('training') && (
            <button
              onClick={() => navigate('/training')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-blue-700 mb-2">📚</div>
              <h3 className="text-lg font-bold text-blue-900 mb-1">Training Management</h3>
              <p className="text-sm text-blue-600">Training, Certifications & Competency</p>
            </button>
          )}

          {/* Audits Module */}
          {isModuleEnabled('audits') && (
            <button
              onClick={() => navigate('/audits')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-purple-700 mb-2">🔍</div>
              <h3 className="text-lg font-bold text-purple-900 mb-1">Audits & Inspections</h3>
              <p className="text-sm text-purple-600">Compliance Audits & Safety Inspections</p>
            </button>
          )}

          {/* Meetings Module */}
          {isModuleEnabled('meetings') && (
            <button
              onClick={() => navigate('/meetings')}
              className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-indigo-700 mb-2">👥</div>
              <h3 className="text-lg font-bold text-indigo-900 mb-1">Safety Meetings</h3>
              <p className="text-sm text-indigo-600">Schedule meetings, toolbox talks & action tracking</p>
            </button>
          )}

          {/* SSEM Module */}
          {isModuleEnabled('ssem') && (
            <button
              onClick={() => navigate('/ssem')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-purple-700 mb-2">📊</div>
              <h3 className="text-lg font-bold text-purple-900 mb-1">SSEM</h3>
              <p className="text-sm text-purple-600">Safety Excellence Maturity Assessment</p>
            </button>
          )}

          {/* Documents Module */}
          {isModuleEnabled('documents') && (
            <button
              onClick={() => navigate('/documents')}
              className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="text-2xl font-bold text-teal-700 mb-2">📄</div>
              <h3 className="text-lg font-bold text-teal-900 mb-1">Document Control</h3>
              <p className="text-sm text-teal-600">Manage SOPs, policies & procedures</p>
            </button>
          )}
        </div>

        {/* Dashboards Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Analytics & Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/dashboard/executive')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-purple-700">📊</div>
                <div>
                  <h3 className="text-lg font-bold text-purple-900">Executive Dashboard</h3>
                  <p className="text-sm text-purple-600">View leading indicators, PSS, and insights</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/dashboard/supervisor')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-blue-700">👷</div>
                <div>
                  <h3 className="text-lg font-bold text-blue-900">Supervisor Dashboard</h3>
                  <p className="text-sm text-blue-600">Mobile-friendly view for frontline supervisors</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Incident Reporting Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Incident Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/report')}
              className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-red-700">🚨</div>
                <div>
                  <h3 className="text-lg font-bold text-red-900">Report Incident</h3>
                  <p className="text-sm text-red-600">Report safety incidents, near misses, observations</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/hazards')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-blue-700">📋</div>
                <div>
                  <h3 className="text-lg font-bold text-blue-900">View Incidents</h3>
                  <p className="text-sm text-blue-600">Track and manage all reported incidents</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyHub;
